import { useState } from "react";
import { Reply, ChevronDown } from "lucide-react";
import { SITE } from "@/data/site";
import { formatRange } from "@/lib/dates";

const dates = (e) => (e.check_in ? formatRange(e.check_in, e.check_out) : "the dates you asked about");
const home = (e) => e.property_name || "one of our homes";
const first = (e) => (e.name || "there").split(" ")[0];
const sign = `Kind regards,\n${SITE.owner}\nPARADISIO RESORTS\n${SITE.phoneDisplay}`;

const TEMPLATES = [
  {
    key: "available",
    label: "Available — here's your quote",
    subject: (e) => `${home(e)} — available for ${dates(e)}`,
    body: (e) =>
      `Hi ${first(e)},\n\nThanks for getting in touch about ${home(e)}.\n\nGood news — ${dates(e)} is available. The total for your stay is $____, which covers the whole stay with no booking fees. A deposit of $____ secures the dates and the balance is due before you arrive.\n\nHappy to answer any questions, or just reply "yes please" and I'll hold it for you.\n\n${sign}`,
  },
  {
    key: "taken",
    label: "Sorry, those dates are taken",
    subject: (e) => `${home(e)} — ${dates(e)}`,
    body: (e) =>
      `Hi ${first(e)},\n\nThanks for your enquiry about ${home(e)}.\n\nUnfortunately ${dates(e)} is already booked. I do have ____ to ____ free, and ____ to ____ after that, if either of those could work for you.\n\nI also have other homes that may suit — just let me know and I'll send you the options.\n\n${sign}`,
  },
  {
    key: "info",
    label: "Need a bit more info",
    subject: (e) => `${home(e)} — a couple of quick questions`,
    body: (e) =>
      `Hi ${first(e)},\n\nThanks for your enquiry about ${home(e)}.\n\nSo I can get you an accurate price, could you let me know:\n\n· Your arrival and departure dates\n· How many adults and children are travelling\n· Anything else I should know about your stay\n\nOnce I have those I'll come straight back to you with availability and the total.\n\n${sign}`,
  },
  {
    key: "holding",
    label: "Thanks — holding those dates",
    subject: (e) => `${home(e)} — holding ${dates(e)} for you`,
    body: (e) =>
      `Hi ${first(e)},\n\nLovely — I'm holding ${home(e)} for ${dates(e)}.\n\nThe total is $____. To confirm, a deposit of $____ can be paid by ____, and the balance is due before you arrive. I'll send arrival details and the key arrangements closer to the date.\n\nAny questions in the meantime, just call me on ${SITE.phoneDisplay}.\n\n${sign}`,
  },
];

export const EnquiryReply = ({ enquiry, onReplied }) => {
  const [open, setOpen] = useState(false);

  const link = (t) =>
    `mailto:${enquiry.email}?subject=${encodeURIComponent(t.subject(enquiry))}&body=${encodeURIComponent(
      t.body(enquiry)
    )}`;

  return (
    <div className="mt-4">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 bg-[#0F2A52] text-white rounded-md px-4 py-2.5 text-sm"
        data-testid={`owner-reply-toggle-${enquiry.id}`}
      >
        <Reply size={15} /> Write a reply
        <ChevronDown size={14} className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid={`owner-reply-templates-${enquiry.id}`}>
          {TEMPLATES.map((t) => (
            <a
              key={t.key}
              href={link(t)}
              onClick={() => onReplied?.(enquiry)}
              className="border border-[#E5E5E5] bg-white rounded-md px-4 py-3 text-sm text-[#0F2A52] hover:border-[#0F2A52]"
              data-testid={`owner-reply-${t.key}-${enquiry.id}`}
            >
              {t.label}
            </a>
          ))}
          <p className="sm:col-span-2 text-xs text-neutral-500 leading-relaxed">
            Opens your email app with the reply already written and addressed to {enquiry.email}. Fill in the
            blanks (____), then send — it comes from your own email address.
          </p>
        </div>
      )}
    </div>
  );
};

export default EnquiryReply;
